'use client';
import React, { useState } from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { Session } from 'next-auth';
import Link from 'next/link';
import { IoIosCloseCircleOutline, IoIosMenu } from 'react-icons/io';
import { isFeatureEnabled } from '~/lib/featureGate';
import { cn } from '~/lib/utils';
import { IconBrandDiscord, IconBriefcase2, IconMail, IconMessage2, IconUsers } from '@tabler/icons-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { IconButton } from '../ui/IconButton';
import { CTAButton } from './cta-button';
import { CustomLink } from './custom-link';
import { Logo } from './logo';
import { Menu, MenuItem } from './navbar-menu';

//@TODO make this a reusable component in its own file
function DiscordLink() {
  return (
    <a
      href="https://discord.gg/fireworks-ai"
      target="__blank"
      className="ml-auto px-4"
      title="Join our Discord for questions and support"
      aria-label="Join the Fireworks.ai discord (Link opens in a new tab)"
    >
      <IconBrandDiscord
        role="img"
        strokeWidth="1.5px"
        aria-hidden="true"
        className="text-foreground-secondary h-6 w-6"
      />
    </a>
  );
}

const navItems = [
  {
    name: 'Models',
    link: '/models',
  },
  {
    name: 'Docs',
    link: 'https://readme.fireworks.ai/docs',
  },
  {
    name: 'Pricing',
    link: '/pricing',
  },

  {
    name: 'Company',
    link: undefined,
    children: [
      {
        name: 'Blog',
        link: '/blog',
        icon: <IconMessage2 className="h-4 w-4 text-neutral-600 dark:text-zinc-400" />,
        description: 'Read about the latest news and updates from Fireworks AI.',
      },
      {
        name: 'Careers',
        link: 'https://jobs.polymer.co/fireworks-ai',
        icon: <IconBriefcase2 className="h-4 w-4 text-neutral-600 dark:text-zinc-400" />,
        description: 'Join our team and help us build the future of data.',
      },
      {
        name: 'Contact',
        link: '/company/contact-us',
        icon: <IconMail className="h-4 w-4 text-neutral-600 dark:text-zinc-400" />,
        description: 'Reach out to us for any questions or feedback.',
      },
      {
        name: 'Team',
        link: '/team',
        icon: <IconUsers className="h-4 w-4 text-neutral-600 dark:text-zinc-400" />,
        description: 'Meet the innovative and driven Fireworks.ai ready to make your project a success.',
      },
    ],
  },
] as const;

const Navbar = ({
  navClassName,
  showFloatingNav = true,
  session,
}: {
  navClassName?: string;
  showFloatingNav?: boolean;
  session?: Session | null;
}) => {
  return (
    <>
      <div
        className={cn(
          'w-full border-b border-transparent bg-white/70 py-4 backdrop-blur-sm max-lg:backdrop-blur-lg dark:border-white/[0.1] dark:bg-zinc-900/[0.2]',
          'relative top-0 z-50 max-md:left-0',
        )}
      >
        <div
          className={cn(
            'relative z-[80] mx-auto flex w-full max-w-7xl flex-row items-center justify-between px-4',
            navClassName,
          )}
        >
          <div className="flex items-center justify-start">
            <Link href="/" className="cursor-pointe mr-10">
              <Logo SVGAttributes={{ role: 'graphics-symbol', 'aria-label': 'Fireworks AI Home' }} />
            </Link>
            <DesktopNav />
          </div>

          <DiscordLink />
          <div className=" hidden items-center space-x-2 md:flex">
            {session ? (
              <UserNav session={session} />
            ) : (
              <CTAButton as={Link} href="/login" className="hidden md:inline-flex" />
            )}
          </div>
          <div className="flex md:hidden">
            <MobileNav session={session} />
          </div>
        </div>
      </div>
      {showFloatingNav && <FloatingNav session={session} />}
    </>
  );
};

export const FloatingNav = ({ session }: { session?: Session | null }) => {
  const { scrollYProgress, scrollY } = useScroll();

  const [visible, setVisible] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', () => {
    if (scrollYProgress.get() < 0.02) {
      setVisible(false);
      // @ts-expect-error private api...
    } else if (scrollY.current > 60) {
      // if (direction < 0) {
      //   setVisible(true);
      // } else {
      //   setVisible(false);
      // }
      setVisible(true);
    }
  });
  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{
          opacity: 1,
          y: -100,
        }}
        animate={{
          y: visible ? 0 : -100,
          opacity: visible ? 1 : 0,
        }}
        transition={{
          duration: 0.2,
        }}
        className="shadow-aceternity fixed inset-x-0 top-4 z-[25] mx-auto hidden max-w-fit items-center justify-center space-x-4 rounded-[14px] border border-transparent bg-white/70 px-4 py-2  backdrop-blur-md lg:flex dark:bg-zinc-950/50"
      >
        <Link href="/" className="cursor-pointer">
          <Logo SVGAttributes={{ role: 'graphics-symbol', 'aria-label': 'Fireworks AI Home' }} />
        </Link>

        {navItems.map((navItem) =>
          navItem.link ? (
            <CustomLink
              key={navItem.name}
              href={navItem.link}
              className={cn('relative text-sm  dark:text-neutral-50 dark:hover:text-neutral-300')}
            >
              {navItem.name}
            </CustomLink>
          ) : (
            navItem.children.map((subItem: any) => (
              <CustomLink
                key={subItem.name}
                href={subItem.link}
                className={cn(
                  'relative text-sm text-zinc-700 hover:text-zinc-900 dark:text-neutral-50 dark:hover:text-neutral-300',
                )}
              >
                {subItem.name}
              </CustomLink>
            ))
          ),
        )}

        {session ? (
          <UserNav session={session} />
        ) : (
          <CTAButton as={Link} href="/login" className="hidden lg:inline-flex" />
        )}
      </motion.div>
    </AnimatePresence>
  );
};
const MobileNav = ({ session }: { session?: Session | null }) => {
  const [open, setOpen] = useState(false);

  const Icon = open ? IoIosCloseCircleOutline : IoIosMenu;

  return (
    <>
      <Icon
        onClick={() => setOpen(!open)}
        className="h-10 w-10 text-zinc-600 hover:text-zinc-500  dark:text-zinc-400 dark:hover:text-zinc-300"
      />
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 top-16 z-50 flex h-screen w-screen flex-col bg-white pt-10 transition duration-200 dark:bg-zinc-900">
            {/* <IoIosCloseCircleOutline className="absolute right-10 top-5 h-8 w-8 " onClick={() => setOpen(!open)} /> */}
            {session?.user ? (
              <>
                <motion.div
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  animate={{
                    x: 0,
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.2,
                    delay: 0 * 0.1,
                  }}
                  className="border-border mx-6 flex justify-between border-b pb-4"
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-sm">{session?.user?.name ?? `Account: ${session?.accountType ?? 'N/A'}`}</p>
                    <p className="text-foreground-secondary">{session?.user?.email}</p>
                  </div>
                  <div>
                    <Avatar className="text-foreground">
                      <AvatarFallback>
                        {session?.user?.name
                          ?.split(' ')
                          .map((n) => n[0]?.toUpperCase() ?? 'U')
                          .join('') ??
                          session?.user?.email?.[0]?.toUpperCase() ??
                          'USER'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </motion.div>
                <motion.div
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  animate={{
                    x: 0,
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.2,
                    delay: 1 * 0.1,
                  }}
                  className="w-full "
                >
                  <CustomLink
                    onClick={() => setOpen(false)}
                    href={'/users'}
                    className="border-border mx-6 flex items-center justify-between border-b p-4 text-sm transition-all hover:bg-zinc-100 hover:underline dark:text-zinc-300 dark:hover:bg-zinc-700 [&[data-state=open]>svg]:rotate-180"
                  >
                    <span>Account Settings</span>
                  </CustomLink>
                </motion.div>
                <motion.div
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  animate={{
                    x: 0,
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.2,
                    delay: 2 * 0.1,
                  }}
                  className="w-full"
                >
                  <CustomLink
                    href={'/logout'}
                    className="border-border mx-6 flex items-center justify-between p-4 text-sm transition-all hover:bg-zinc-100 hover:underline dark:text-zinc-300 dark:hover:bg-zinc-700 [&[data-state=open]>svg]:rotate-180"
                  >
                    <span>Logout</span>
                  </CustomLink>
                </motion.div>
              </>
            ) : (
              <motion.div
                initial={{
                  opacity: 0,
                  x: -20,
                }}
                animate={{
                  x: 0,
                  opacity: 1,
                }}
                transition={{
                  duration: 0.2,
                  delay: 0 * 0.1,
                }}
                className="mx-6 flex justify-between pb-4"
              >
                <CTAButton as={Link} href="/login" className="w-full text-center">
                  Login / Sign up
                </CTAButton>
              </motion.div>
            )}
            <Accordion type="single" collapsible className="mt-10 w-full px-6">
              {navItems.map((navItem: any, idx: number) => (
                <motion.div
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  animate={{
                    x: 0,
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.2,
                    delay: (idx + 3) * 0.1,
                  }}
                  key={navItem.name}
                >
                  <AccordionItem value={navItem.name} className={cn({ 'border-b-0': navItems.length - 1 === idx })}>
                    {!!navItem.link && (
                      <CustomLink
                        onClick={() => setOpen(false)}
                        href={navItem.link || '/'}
                        className="flex w-full flex-1 items-center p-4 text-sm transition-all hover:bg-zinc-100 hover:underline dark:text-zinc-300 dark:hover:bg-zinc-700 [&[data-state=open]>svg]:rotate-180"
                      >
                        <span>{navItem.name}</span>
                        {navItem.badge && (
                          <span className="bg-primary-400 border-primary-500 absolute -right-14 top-1 ml-1 inline-block rounded-xl border px-1 py-0.5 text-xs capitalize text-white">
                            {navItem.badge}
                          </span>
                        )}
                      </CustomLink>
                    )}
                    {navItem.children && (
                      <>
                        <AccordionTrigger className="flex w-full flex-1 items-center justify-between p-4 text-sm transition-all hover:underline dark:text-zinc-300 [&[data-state=open]>svg]:rotate-180">
                          <span>{navItem.name}</span>
                          {navItem.badge && (
                            <span className="bg-primary-400 border-primary-500 absolute -right-14 top-1 ml-1 inline-block rounded-xl border px-1 py-0.5 text-xs capitalize text-white">
                              {navItem.badge}
                            </span>
                          )}
                        </AccordionTrigger>
                        <AccordionContent>
                          {navItem.children.map((subItem: any, index: number) => (
                            <AccordionItem
                              key={subItem.name}
                              value={subItem.link}
                              className={cn({ 'border-t': index === 0 })}
                            >
                              <CustomLink
                                onClick={() => setOpen(false)}
                                href={subItem.link || '/'}
                                className="flex w-full flex-1 items-center p-4 text-sm transition-all hover:bg-zinc-100 hover:underline dark:text-zinc-300 dark:hover:bg-zinc-700 [&[data-state=open]>svg]:rotate-180"
                              >
                                {subItem.icon}
                                <span className="ml-2">{subItem.name}</span>
                                {subItem.badge && (
                                  <span className="bg-primary-400 border-primary-500 absolute -right-14 top-1 ml-1 inline-block rounded-xl border px-1 py-0.5 text-xs capitalize text-white">
                                    {subItem.badge}
                                  </span>
                                )}
                              </CustomLink>
                            </AccordionItem>
                          ))}
                        </AccordionContent>
                      </>
                    )}
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

const DesktopNav = () => {
  const [active, setActive] = useState<string | null>(null);

  return (
    <Menu setActive={setActive}>
      {navItems.map((navItem, idx: number) => {
        if ('children' in navItem) {
          return (
            <MenuItem
              setActive={setActive}
              key={`link-${idx}`}
              active={active}
              item={navItem.name}
              href={navItem.link}
              className="z-50"
            >
              <div className="flex min-w-20 flex-col space-y-2">
                {navItem.children.map((el: any, index: number) => (
                  <CustomLink
                    className="flex space-x-2 rounded-md p-2 text-base hover:bg-gray-100 dark:hover:bg-zinc-900"
                    key={`link-${idx}-${index}`}
                    href={el.link}
                  >
                    {el.icon && (
                      <div className="shadow-aceternity flex h-8 w-8 items-center justify-center rounded-md bg-white dark:bg-zinc-800">
                        {el.icon}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-zinc-700 hover:text-zinc-900 dark:text-zinc-200 dark:hover:text-zinc-50">
                        {el.name}{' '}
                      </span>
                      <span className="mt-1 w-60 whitespace-normal text-xs text-zinc-700 dark:text-zinc-400">
                        {el.description}
                      </span>
                    </div>
                  </CustomLink>
                ))}
              </div>
            </MenuItem>
          );
        }

        return (
          <CustomLink key={`link-${idx}`} href={navItem.link}>
            <span className="text-base text-zinc-700 hover:text-zinc-900 dark:text-zinc-200 dark:hover:text-zinc-50">
              {navItem.name}
            </span>
          </CustomLink>
        );
      })}
    </Menu>
  );
};

export default Navbar;

function UserNav({ session }: { session?: Session | null }) {
  const billingEnabled = isFeatureEnabled('billing', session);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton variant="secondary">
          {session?.user?.name
            ?.split(' ')
            .map((n) => n[0]?.toUpperCase() ?? 'USER')
            .join('') ??
            session?.user?.email?.[0]?.toUpperCase() ??
            'USER'}
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm leading-none">{session?.user?.email}</p>
            <p className="text-muted-foreground text-xs leading-none">{session?.user?.name ?? session?.accountType}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link href="/users">
            <DropdownMenuItem className="cursor-pointer">Account settings</DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link href="/api-keys">
            <DropdownMenuItem className="cursor-pointer">API keys</DropdownMenuItem>
          </Link>
          {billingEnabled && (
            <Link href="/billing">
              <DropdownMenuItem className="cursor-pointer">Billing</DropdownMenuItem>
            </Link>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <Link href="/logout">
          <DropdownMenuItem className="cursor-pointer">Sign out</DropdownMenuItem>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
